import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Button, Card} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';

import {uniq} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetAdvertisementPreflightQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {usePermissions, useVehiclePhotosPermissions} from '@dms/shared';

import {useAdvertisementPlatformFilter} from '../hooks/useAdvertisementPlatformFilter';
import {useOpenPlatformDetail} from '../hooks/useOpenPlatformDetail';
import {getAdvertisingPreflightIssueText} from '../utils/getAdvertisingPreflightIssueText';
import {openPlatformPreflightDialog} from '../utils/openPlatformPreflightDialog';

export function AdvertisementPublishErrors(props: {vehicleId: string}) {
  const {platforms} = useAdvertisementPlatformFilter({
    vehicleId: props.vehicleId,
    skipPolling: true,
  });
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const {data: participation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });
  const [canEditPlatform, canEditVehicle, canReadAdvertisingSettings] = usePermissions({
    permissionKeys: ['editPlatformSettings', 'updateVehicle', 'readAdvertisingSettings'],
    scopes: {
      editPlatformSettings: {participation, branchId: vehicle?.branchId},
      updateVehicle: {participation, branchId: vehicle?.branchId},
      readAdvertisingSettings: {participation, branchId: vehicle?.branchId},
    },
  });
  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({vehicleId: props.vehicleId});
  const openPlatformDetail = useOpenPlatformDetail({
    vehicleId: props.vehicleId,
    isDisabled: !canEditPlatform,
  });
  const {data: preflight} = useGetAdvertisementPreflightQuery(
    canReadAdvertisingSettings
      ? {
          vehicleId: props.vehicleId,
          publishVgpViaAdvertisingBridge: isFeatureEnabled(
            featureFlags.ADVERTISEMENT_VGP_PROMPT_BRIDGE
          ),
          publishOtomotoViaAdvertisingBridge: isFeatureEnabled(
            featureFlags.ADVERTISEMENT_OTOMOTO_PROMPT_BRIDGE
          ),
          publishAutocarisViaAdvertisingBridge: isFeatureEnabled(
            featureFlags.ADVERTISEMENT_AUTOCARIS_PROMPT_BRIDGE
          ),
          publishMercedesNaSkladeViaAdvertisingBridge: isFeatureEnabled(
            featureFlags.ADVERTISEMENT_MERCEDES_NA_SKLADE_PROMPT_BRIDGE
          ),
          publishHyundaiPromiseViaAdvertisingBridge: isFeatureEnabled(
            featureFlags.ADVERTISEMENT_HYUNDAI_PROMISE_PROMPT_BRIDGE
          ),
          publishAutoplacPlViaAdvertisingBridge: isFeatureEnabled(
            featureFlags.ADVERTISEMENT_AUTOPLAC_PL_PROMPT_BRIDGE
          ),
        }
      : skipToken
  );
  const platformTitles = new Map(
    platforms.map(({platform}) => [platform.code, platform.title] as const)
  );
  const blockedResults =
    preflight?.results.filter(
      (result) => result.status !== 'ready' && platformTitles.has(result.platformCode)
    ) ?? [];

  if (blockedResults.length === 0) {
    return null;
  }

  return (
    <Card
      title={i18n.t('page.advertisement.preview.beforeYouPublish')}
      variant="inlineWhite"
      colorStripe="red"
      hasSeparator
      data-testid={testIds.vehicles.publishing('publishErrors')}
    >
      <VStack spacing={3} align="stretch">
        {blockedResults.map((result) => {
          const platformTitle = platformTitles.get(result.platformCode) ?? result.platformCode;
          const openPlatform = () => openPlatformDetail(result.platformCode, platformTitle);
          const hasDetails = result.issues.length > 0;
          const action = () => {
            if (!hasDetails) {
              openPlatform();
              return;
            }
            openPlatformPreflightDialog({
              vehicleId: props.vehicleId,
              platformCode: result.platformCode,
              platformTitle,
              issues: result.issues,
              canEditVehicle,
              canEditPlatform,
              canReadPhotos: canReadVehicleAlbumPhotos,
              onOpenPlatformDetail: openPlatform,
            });
          };
          return (
            <HStack key={result.platformCode} spacing={3}>
              <VStack spacing={1} align="stretch" width="100%">
                <HStack spacing={3} align="center">
                  <Box
                    width={2}
                    height={2}
                    borderRadius="medium"
                    backgroundColor="severity.danger"
                    flexShrink={0}
                  />
                  <Text>{i18n.t('page.advertisement.preview.issueTitles.preflightBlocked')}</Text>
                </HStack>
                <Box paddingLeft={5}>
                  <Text size="small" color="secondary">
                    {i18n.t('page.advertisement.preview.issues.preflight_blocked', {
                      platform: platformTitle,
                    })}
                  </Text>
                  {uniq(result.issues.map(getAdvertisingPreflightIssueText)).map((text) => (
                    <Text key={text} size="small" color="secondary">
                      {text}
                    </Text>
                  ))}
                </Box>
              </VStack>
              <Button
                variant="link"
                size="default"
                title={
                  hasDetails
                    ? i18n.t('general.labels.details')
                    : `${i18n.t('page.advertisement.actions.editPlatform')} ${platformTitle}`
                }
                onClick={action}
              />
            </HStack>
          );
        })}
      </VStack>
    </Card>
  );
}
